import type { RouteLocationNormalized } from 'vue-router';

/**
 * Resolves whether a given route targets a plan-restricted module or sub-page and,
 * if so, which permission key gates it.
 *
 * When the user lands directly on a gated page (deep-link / refresh), the navigation is
 * let through and the page shows the upsell inline (in place of its content, header still
 * visible). `restrictedRoutePermissionKey` exposes the gated key for the current route so
 * the `Page` component can render that inline upsell.
 */
export default function useRouteUpsellGuard() {
    const { modules } = useModules();
    const { isUpsellRestricted } = useUpsellAccess();
    const route = useRoute();

    const moduleForPath = (path: string) => {
        const prefix = path.split('/')[1];
        if (!prefix) return undefined;
        return (
            modules.value.find((m) => m.basePath && path.startsWith(m.basePath)) ??
            modules.value.find((m) => m.key === prefix) ??
            (prefix === 'bankaccount' ? modules.value.find((m) => m.key === 'invoicing') : undefined)
        );
    };

    /**
     * Gated permission key for the route, checking the module AND its sub-pages. Used by
     * the middleware to block in-app navigation to any restricted page.
     */
    const findRestrictedPermissionKey = (to: RouteLocationNormalized | { path: string }): string | null => {
        const module = moduleForPath(to.path);
        if (!module) return null;

        const modulePermKey = module.permissionKey ?? `${module.key}:access`;
        if (isUpsellRestricted(modulePermKey)) return modulePermKey;

        if (module.children) {
            const child = module.children.find((c) => c.to && to.path.startsWith(c.to));
            if (child?.permission && isUpsellRestricted(child.permission)) return child.permission;
        }

        return null;
    };

    /**
     * Gated permission key for the current route, or null. Checks the module first, then the
     * matching sub-page (child) — so the Page component renders the inline upsell on a gated
     * sub-page too (e.g. /visibility/reviews), not only on a gated module root.
     */
    const restrictedRoutePermissionKey = computed(() => {
        const module = moduleForPath(route.path);
        if (!module) return null;
        const modulePermKey = module.permissionKey ?? `${module.key}:access`;
        if (isUpsellRestricted(modulePermKey)) return modulePermKey;
        const child = module.children?.find((c) => c.to && route.path.startsWith(c.to));
        if (child?.permission && isUpsellRestricted(child.permission)) return child.permission;
        return null;
    });

    return {
        findRestrictedPermissionKey,
        restrictedRoutePermissionKey,
    };
}
